// ADR-0030: a trip's legs carry nights; their dates are derived, never stored.

/** What the server joins the legs' names with in `Trip.destination`. */
export const SUMMARY_SEPARATOR = ' · ';

/** "Samarcanda, Uzbekistán" → "Samarcanda": the name a person says. */
export function shortName(name: string): string {
  return name.split(',')[0].trim();
}

export function summaryName(legs: readonly { name: string }[]): string {
  return legs.map(l => l.name).join(SUMMARY_SEPARATOR);
}

/** `total` nights spread over `count` legs, the remainder on the last ones. */
export function evenNights(count: number, total: number): number[] {
  if (count <= 0) return [];
  const base = Math.floor(total / count);
  const extra = total - base * count;
  return Array.from({ length: count }, (_, i) => base + (i >= count - extra ? 1 : 0));
}

/** The first and last day number (1-based) of each leg. The last leg keeps the final day. */
export function legDayRanges(nights: readonly number[]): { from: number; to: number }[] {
  let day = 1;
  return nights.map((n, i) => {
    const from = day;
    day += n;
    return { from, to: i === nights.length - 1 ? from + n : Math.max(from, from + n - 1) };
  });
}

/**
 * The leg day N (1-based) belongs to: the one whose nights cover it. The travel
 * day is the new leg's — you sleep there. Same rule as the server's
 * `destinationForDay`; past the last night it stays on the last leg.
 */
export function destinationForDay<T extends { nights: number }>(legs: readonly T[], dayNumber: number): T | null {
  if (legs.length === 0) return null;
  let start = 0;
  for (const leg of legs) {
    if (dayNumber - 1 < start + leg.nights) return leg;
    start += leg.nights;
  }
  return legs[legs.length - 1];
}

/** The leg that begins on day N, for the day strip's labels. Nothing with a single leg. */
export function legStartingOn<T extends { nights: number }>(legs: readonly T[], dayNumber: number): T | null {
  if (legs.length < 2) return null;
  const leg = destinationForDay(legs, dayNumber);
  return dayNumber === 1 || leg !== destinationForDay(legs, dayNumber - 1) ? leg : null;
}

/** Where day N is: "Biskek", or "Samarcanda → Biskek" on the travel day. Nothing with a single leg. */
export function dayPlaceLabel(legs: readonly { name: string; nights: number }[], dayNumber: number): string | null {
  if (legs.length < 2) return null;
  const leg = destinationForDay(legs, dayNumber);
  if (!leg) return null;
  const prev = dayNumber > 1 ? destinationForDay(legs, dayNumber - 1) : null;
  return prev && prev !== leg ? `${shortName(prev.name)} → ${shortName(leg.name)}` : shortName(leg.name);
}

/** Position in `legs` of the leg day N belongs to; -1 with no legs. */
export function legIndexForDay<T extends { nights: number }>(legs: readonly T[], dayNumber: number): number {
  const leg = destinationForDay(legs, dayNumber);
  return leg ? legs.indexOf(leg) : -1;
}

type LocatedLeg = { nights: number; latitude: number | null; longitude: number | null };

/**
 * The leg a place falls under in the map's leg filter: its first day's, or —
 * with no day yet — the leg whose destination is nearest, so the places still
 * to plan show up where they would be planned. Null when nothing can tell (a
 * single leg, or a leg with no coordinates): the place then shows under every leg.
 */
export function legOfPlace(
  legs: readonly LocatedLeg[],
  dayNumbers: readonly number[],
  at: { lat: number; lng: number },
): number | null {
  if (legs.length < 2) return null;
  if (dayNumbers.length > 0) return legIndexForDay(legs, Math.min(...dayNumbers));
  if (legs.some(l => l.latitude == null || l.longitude == null)) return null;
  // Equirectangular is plenty to tell two destinations apart.
  const lngScale = Math.cos((at.lat * Math.PI) / 180);
  let best = 0;
  let bestDist = Infinity;
  legs.forEach((leg, i) => {
    const dLat = leg.latitude! - at.lat;
    const dLng = (leg.longitude! - at.lng) * lngScale;
    const dist = dLat * dLat + dLng * dLng;
    if (dist < bestDist) { bestDist = dist; best = i; }
  });
  return best;
}
